<template>
  <div
    :class="[
      $style.base,
      {
        [$style.smallScreen]: $screen === 's'
      }
    ]"
  >
    <Flex
      :direction="'column'"
      :gap="2"
    >
      <BaseHeading
        :size="$screen === 's' ? 'l' : 'xl'"
        center
      >
        {{ $t('login.heading') }}
      </BaseHeading>
      <BaseForm @submit="onSubmit">
        <Flex :direction="'column'">
          <BaseInput
            v-model="email"
            :label="$t('global.email')"
            type="email"
            name="email"
            required
            v-test="'login-email'"
          />
          <div :class="$style.passwordContainer">
            <BaseInput
              v-model="password"
              :label="$t('global.password')"
              type="password"
              name="password"
              required
              v-test="'login-password'"
            />
            <div :class="$style.forgotPassword">
              <BaseText
                :routerLink="{ name: 'forgot-password' }"
                v-test="'btn-forgot-password'"
              >
                {{ $t('login.forgot_password') }}
              </BaseText>
            </div>
          </div>
          <BaseButton
            :loading="isLoading"
            size="l"
            submitForm
            fullWidth
            v-test="'login-submit'"
          >
            {{ $t('login.button') }}
          </BaseButton>
          <BaseText
            v-if="loginFailed"
            color="error"
            v-test="'error-unauthorized'"
          >
            {{ $t('login.unknown_user_wrong_password') }}
          </BaseText>
          <BaseText
            v-if="loginBlocked"
            color="error"
            v-test="'error-blocked'"
          >
            {{ $t('login.too_many_attempts') }}
          </BaseText>
        </Flex>
      </BaseForm>
      <div
        v-if="!isNativeAppIOS"
        :class="$style.nav"
      >
        <BaseText inline>{{ $t('login.signup_1') }}&nbsp; </BaseText>
        <BaseText
          :routerLink="{ name: 'signup' }"
          bold
          v-test="'btn-signup'"
        >
          {{ $t('login.signup_2') }}
        </BaseText>
      </div>
    </Flex>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import axios from '@/axios';
import {
  NativeAppMessage,
  sendNativeAppMessage,
} from '@/helpers/native-app-bridge';
import { redirectRoute } from '@/router/helpers';
import { useNativeAppStore } from '@/stores/native-app';
import { useSessionStore } from '@/stores/session';
import { useUserStore } from '@/stores/user';
import { credentials } from './credentials';

export default defineComponent({
  setup() {
    const { isNativeAppIOS } = useNativeAppStore();
    return { isNativeAppIOS, credentials };
  },
  data() {
    return {
      email: '',
      password: '',
      isLoading: false,
      loginFailed: false,
      loginBlocked: false,
    };
  },
  async mounted() {
    const unlockToken = this.$route.query.unlock_token;
    if (unlockToken) {
      try {
        await axios.get(`/sessions/unlock?unlock_token=${unlockToken}`);
        // Remove the unlock token from URL to clean it up
        this.$router.replace({
          name: 'login',
          query: { ...this.$route.query, unlock_token: undefined },
        });
      } catch (error) {
        // Silently handle unlock failure
        // biome-ignore lint/suspicious/noConsole: Needed for debugging
        console.error('Unlock failed:', error);
        // Still clean up the URL
        this.$router.replace({
          name: 'login',
          query: { ...this.$route.query, unlock_token: undefined },
        });
      }
    }
  },
  methods: {
    onSubmit() {
      this.authenticate();
    },
    authenticate() {
      this.isLoading = true;
      this.loginFailed = false;
      this.loginBlocked = false;

      axios
        .post('/sessions', {
          user: {
            email: this.email,
            password: this.password,
          },
        })
        .then(({ data }) => {
          if (data.error) {
            this.loginFailed = true;
            this.isLoading = false;
          } else {
            if (data.two_factor_required) {
              credentials.email = this.email;
              credentials.password = this.password;
              credentials.authenticator_mode = data.authenticator_mode;

              this.$router.push({
                name: 'two-factor-auth',
              });
            } else {
              sendNativeAppMessage({
                message: NativeAppMessage.UserLogin,
              });
              const { fetchAllData } = useSessionStore();
              fetchAllData().then(() => {
                const { user } = useUserStore();
                if (user) {
                  this.$router.push(
                    redirectRoute({ query: this.$route.query }),
                  );
                } else {
                  this.loginFailed = true;
                  this.isLoading = false;
                }
              });
            }
          }
        })
        .catch((response) => {
          this.isLoading = false;
          if (response.status === 429) {
            this.loginBlocked = true;
          } else {
            this.loginFailed = true;
          }
        });
    },
  },
});
</script>

<style
  lang="scss"
  module
>
.base {
  height: 100%;
}

.bottom {
  .base:not(.smallScreen) & {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .base.smallScreen & {
    & > * {
      display: block;

      &:not(:last-child) {
        margin-bottom: $spacing;
      }
    }
  }
}

.passwordContainer {
  position: relative;
}

.forgotPassword {
  position: absolute;
  top: 0;
  right: 0;
}
.nav {
  display: flex;
  justify-content: center;
}
</style>
